import { cn } from "@nexirt/ui";
import { COPILOT_CHIPS, CHIP_PREFILL_EVENT } from "../lib/copilot-chips";

/**
 * CopilotComposerChips — Rev 25.5 sibling-render for the hero suggestion
 * chips. Decouples chip placement from CopilotComposer's column so the
 * hero layout can place chips below the robot at desktop (right column)
 * while keeping them below the composer at mobile (single column).
 *
 * Communicates with CopilotComposer via the `nexirt:copilot:chip-
 * prefill` window event. Composer listens, sets its textarea value to
 * detail.prompt, and focuses. Same UX as Rev 25.2's onChip handler;
 * now driven by an event so the chip render site can move freely.
 *
 * `layout="grid"` renders 2-col responsive (1-col at <360px); `layout=
 * "rail"` renders horizontal scroll with snap. The hero uses `grid` at
 * desktop and `rail` at mobile (chosen by the parent via the CSS
 * grid-area or by passing the prop explicitly per breakpoint).
 */
export function CopilotComposerChips({
  layout = "grid",
}: {
  layout?: "grid" | "rail";
}) {
  function pickChip(prompt: string) {
    window.dispatchEvent(
      new CustomEvent(CHIP_PREFILL_EVENT, { detail: { prompt } }),
    );
  }

  return (
    <div
      data-copilot-chips
      data-chip-layout={layout}
      className={cn(
        layout === "grid"
          ? "nx-copilot-chip-grid"
          : "nx-copilot-chip-rail",
      )}
      role="list"
      aria-label="Suggested prompts"
    >
      {COPILOT_CHIPS.map(({ icon: Icon, label, prompt }) => (
        <button
          key={label}
          type="button"
          role="listitem"
          data-copilot-chip
          data-chip-prompt={prompt}
          onClick={() => pickChip(prompt)}
          className={cn(
            "inline-flex items-center gap-2 rounded-nx2 border border-nx-line bg-nx-surface-2/70 px-3 py-2",
            "text-left text-xs text-nx-text-dim transition-colors duration-150 ease-nx",
            "hover:border-nx-accent hover:bg-nx-accent-soft hover:text-nx-text",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nx-accent",
            layout === "rail" && "shrink-0 snap-start",
          )}
        >
          <Icon
            className="h-3.5 w-3.5 shrink-0 text-nx-accent-2"
            strokeWidth={2.2}
            aria-hidden
          />
          <span className="truncate">{label}</span>
        </button>
      ))}
    </div>
  );
}

export default CopilotComposerChips;
